import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export const BRAND_MINT = '#00E5A0'

/**
 * Compliance (e-signature) badge color treatment — shared by the audit-log
 * category badge, the F5 signatory "In-app signature" badge, and the admin
 * org-verification "E-signed" badge so all three stay in sync.
 */
export const COMPLIANCE_BADGE = 'bg-teal-500/10 text-teal-600 dark:text-teal-400'

/**
 * Badge pill shape — the standard status/chip pill used across the portal.
 * Compose with a color treatment (e.g. COMPLIANCE_BADGE or STATUS_* colors)
 * via `badgePill` so the shape stays single-sourced.
 */
const BADGE_PILL = 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium'

/** Build a pill badge: shared pill shape + color treatment(s). */
export function badgePill(...treatments: ClassValue[]) {
  return cn(BADGE_PILL, ...treatments)
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a price in cents as a compact string, trimming trailing ".00"
 * (e.g. 700 → "7", 525 → "5.25", 1200 → "12").
 */
export function formatCents(cents: number): string {
  return (cents / 100).toFixed(2).replace(/\.00$/, '')
}

/**
 * Format a whole-currency amount with its proper symbol (e.g. €100,000.00).
 * The symbol comes from Intl (derived from the currency code), so every ISO
 * 4217 code is covered — no hand-rolled symbol map. Unknown 3-letter codes
 * render via Intl with the code as display (e.g. "XYZ 1,234.50"); invalid/
 * empty codes (which Intl rejects) fall back to the DB-provided symbol, then
 * the code as a last resort. Uses the runtime locale by default (per the
 * i18n plan, no hardcoded en-US); pass `locale` (e.g. 'en-US') to pin the
 * formatting — tests use this so they don't depend on the Node default.
 */
export function formatCurrencyAmount(amount: number, currency: string, fallbackSymbol?: string | null, locale?: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    // Unknown currency code (Intl throws RangeError) — fall back to the
    // DB-provided symbol, or the code itself as a last resort.
    const formatted = amount.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${fallbackSymbol || currency}${formatted}`
  }
}

/**
 * Format a price in cents with its currency symbol (e.g. 52500 + 'EUR' → "€525.00").
 * Intl-derived symbol; unknown/empty codes fall back to USD-style '$' via
 * formatCurrencyAmount. `locale` is passed through — tests pin 'en-US' so
 * they don't depend on the Node runtime default. Single source of truth for
 * the billing/payments pages that each used to define their own formatter.
 */
export function formatCentsWithCurrency(cents: number, currency?: string | null, fallbackSymbol?: string | null, locale?: string): string {
  return formatCurrencyAmount(cents / 100, currency || 'USD', fallbackSymbol, locale)
}

/**
 * Render a country's flag as an emoji from its ISO-3166-1 alpha-2 code.
 * Zero-dependency (no external flag CDN, works offline). Returns 🌐 for
 * unknown/empty codes.
 */
export function flagEmoji(iso2?: string | null): string {
  if (!iso2 || iso2.length !== 2) return '🌐'
  return String.fromCodePoint(...[...iso2.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
}
